import { useState, useMemo } from 'react'
import { Header, InlineHeader, InlineSection, HeaderRenamePillButton } from '@/components/DashboardComponents'
import { Button, Textbox, RoundIconButton } from '@/form/FormComponents'
import { useNavigate, useLoaderData, useRouteLoaderData, useRevalidator, useParams, NavLink, Outlet } from 'react-router'
import { GenericEmptyListPlaceholder } from '@/DashboardLayout'
import { List, ListItem, ItemColumn } from '@/components/List'
import { formatDate } from '@/shared/utils'
import { Icon } from '@/components/Icon'
import { useDatabaseSync } from '@/systems/DatabaseSync'
import { usePrivacyMode } from '@/systems/Authentication'
import { useMenu } from '@/components/Menu'
import { PreviewSessionItem } from '@/pages/ScenarioOverview'
import { Modal, ModalContent, ModalButtons, useModal, useErrorModal } from '@/components/Modal'
import { FormProvider, FormSubmitProvider } from '@/form/FormProvider'
import { publicAction } from '@/data/loaders'
import { markdown } from 'markdown'

export const useRenameAssessmentModal = () => {
  const revalidator = useRevalidator();

  const [openRenameAssessmentModal] = useModal(function ({ context, closeModal, setLoading }) {
    const { assessment_id, course_id, title } = context;

    function submitRenameAssessment({formatted_data, focusError}) {
      setLoading(true);
  
      publicAction("renameAssessment", { id: assessment_id, course_id, title: formatted_data.title }).then(function(data) {
        closeModal();
        revalidator.revalidate();
      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "title", error: "Something went wrong, please try again."});
      });
    }

    return (
      <Modal title="Rename assessment">
        <FormProvider onSubmit={submitRenameAssessment}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="title" onEnter={handleSubmit} default_value={title} required="Please enter an assessment title" />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  return [openRenameAssessmentModal];
}

export const useDeleteAssessmentModal = () => {
  const revalidator = useRevalidator();
  const [openErrorModal] = useErrorModal();

  const [openDeleteAssessmentModal] = useModal(({ context, closeModal, setLoading }) => {
    const { assessment_id, course_id, callback } = context;

    function submitDeleteAssessment() {
      setLoading(true);
  
      publicAction("deleteAssessment", { id: assessment_id, course_id }).then(function(data) {
        closeModal();
        if (callback) {
          callback();
        } else {
          revalidator.revalidate();
        }
      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      });
    }

    return (
      <Modal title="Delete assessment?">
        <ModalContent>
          {`This will delete all learner submissions, feedback, and scoring for this assessment. You can't undo this.`}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Confirm" className="destructive" onClick={submitDeleteAssessment}/>
        </ModalButtons>
      </Modal>
    )
  })

  return [openDeleteAssessmentModal];
}

export function Assessment() {
  const navigate = useNavigate();
  const { course_id, assessment_id, organization_id } = useParams();
  const { assessment } = useLoaderData().data.value;

  const [openRenameAssessmentModal] = useRenameAssessmentModal();
  const [openDeleteAssessmentModal] = useDeleteAssessmentModal();

  const handleRename = () => {
    openRenameAssessmentModal({ assessment_id: assessment._id, course_id: assessment.course_id, title: assessment.data.title })
  }

  const handleDelete = () => {
    openDeleteAssessmentModal({ assessment_id: assessment._id, course_id: assessment.course_id, callback: () => {
      navigate(`/courses/${course_id}`);
    } })
  }

  const menuItems = [
    {
      label: 'Rename',
      icon: 'rename-outline',
      onClick: handleRename,
    },
    "separator",
    {
      label: 'Delete',
      icon: 'trash-can-outline',
      onClick: handleDelete,
    }
  ]

  const base = (organization_id ? `/organizations/${organization_id}` : '') + `/courses/${course_id}/assessments/${assessment_id}`;

  const tabs = [
    { to: base, name: "Overview", end: true },
    { to: base + "/submissions", name: "Submissions" }
  ];

  return (
    <>
      <Header title={<HeaderRenamePillButton text={assessment.data.title} onClick={handleRename} />} backButton={`/courses/${course_id}`} tabs={tabs} menuItems={menuItems} />
      <Outlet/>
    </>
  )
}

export function AssessmentOverview() {
  const navigate = useNavigate();
  const { course_id } = useParams();
  const { assessment, preview_sessions, completion_counts } = useRouteLoaderData("assessment_overview").data.value;

  return (
    <div className="dashboard_content_inner nearest_scrollable_container">
      <div className="dashboard_content_container">
        <div className="scenario_overview_container">
          <div>
            <InlineHeader title="Assessment details">
              <Button icon="pencil-outline" className="hollow" backgroundColor="var(--header)" text="Edit assessment" onClick={() => {navigate(`/courses/${course_id}/assessments/${assessment._id}/edit`)}}/>
            </InlineHeader>
            <InlineSection>
              <div className="text_section">
                <div>Due date</div>
                <div className="text">{assessment.data.due_date ? formatDate(assessment.data.due_date) : "—"}</div>
              </div>
              <div className="text_section">
                <div>Instructions</div>
                {assessment.data.description ? (
                  <div className="text markdown collapse_padding" dangerouslySetInnerHTML={{__html: markdown.toHTML(assessment.data.description)}}></div>
                ) : (
                  <div className="text">—</div>
                )}
              </div>
              <div className="text_section">
                <div>Scenario</div>
                <div className="pills">
                  {(assessment._scenario_file && assessment.scenario_version_id) ? (
                    <NavLink to={`/scenarios/${assessment.scenario_id}`} className="pill clickable masked">
                      <div>{!assessment.scenario_version_id ? <Icon name="forum-outline-edit"/> : <Icon name="forum-outline" style={{fill: "var(--scenario_file_color)"}}/>}<span>{assessment._scenario_file.title}</span></div>
                    </NavLink>
                  ) : (
                    <div className="pill masked">
                      <div>{!assessment.scenario_version_id ? <Icon name="forum-outline-edit"/> : <Icon name="forum-outline" style={{fill: "var(--scenario_file_color)"}}/>}<span>{assessment.scenario_data?.title}</span></div>
                    </div>
                  )}
                </div>
              </div>
              <div className="text_section">
                <div>Rubrics</div>
                {assessment.rubrics && assessment.rubrics.length > 0 ? (
                  <div className="pills">
                    {assessment.rubrics.map((r, index) => {
                      const title = r.data?.title;
                      const contents = <div>
                        <Icon name={r._id ? (r._id.startsWith("default") ? "sapient logo" : "bullseye-arrow") : "bullseye-arrow-edit"} style={{fill: r._id ? (r._id.startsWith("default") ? "" : "var(--rubric_file_color)") : "var(--rubric_file_color)"}}/><span>{title ? title : <i>{"Rubric just for this assessment"}</i>}</span>
                      </div>
                      if (r.file && !r._id?.startsWith("default")) {
                        return <NavLink to={`/rubrics/${r._id}`} key={r._id || index} className="pill clickable">{contents}</NavLink>;
                      } else {
                        return <div key={r._id || index} className="pill">{contents}</div>;
                      }
                    })}
                  </div>
                ) : (
                  <div className="text">None</div>
                )}
              </div>
            </InlineSection>
          </div>
          <div>
            <div>
              <InlineHeader title="Submissions">
                <Button text="Review submissions" className="primary" onClick={() => {navigate(`/courses/${course_id}/assessments/${assessment._id}/submissions`)}}/>
                {/* <RoundIconButton icon="chevron-right" onClick={() => {navigate(`/courses/${course_id}/assessments/${assessment._id}/submissions`)}}/> */}
              </InlineHeader>
              <InlineSection halfMarginTop={true}>
                <div className="assessment_completion_large">
                  <div>
                    <div>{completion_counts.assigned || 0}</div>
                    <div>Assigned</div>
                  </div>
                  <div>
                    <div>{completion_counts.submitted || 0}</div>
                    <div>Completed</div>
                  </div>
                  <div>
                    <div>{completion_counts.returned || 0}</div>
                    <div>Returned</div>
                  </div>
                </div>
              </InlineSection>
              <InlineHeader title="Previews">
                <Button icon="play-outline" className="hollow" backgroundColor="var(--header)" text="Preview" onClick={() => {navigate(`/courses/${course_id}/assessments/${assessment._id}/preview`)}}/>
              </InlineHeader>
              <InlineSection halfMarginTop={true}>
                {preview_sessions.length > 0 ? (
                  <>
                    <List columns={[{ name: "Date" }, { name: "Person", width: 123 }]} hide_header={true} menu={true} compact={true}>
                      {preview_sessions.slice(0, 5).map(session => <PreviewSessionItem key={session._id} session={session} scenario_version_id={assessment.scenario_version_id} assessment_scenario_version_id={assessment.assessment_scenario_version_id} is_editor={true} is_assessment={true} />)}
                    </List>
                    {preview_sessions.length > 5 && <div className="extra_courses_count links"><a onClick={() => {navigate(`/courses/${course_id}/assessments/${assessment._id}/previews`)}} tabIndex={0}>See all previews</a></div>}
                  </>
                ) : <GenericEmptyListPlaceholder text="No previews"/>}
              </InlineSection>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function AssessmentSubmissions() {
  const privacyMode = usePrivacyMode();
  const { course_id } = useParams();

  const grader = useLoaderData().data.value;
  const assessment = grader.assessment;

  const just_students = useDatabaseSync({ query: {collection: "course_memberships", course_id: assessment.course_id}, original_value: grader.students, enabled: true }).filter(a => !a.role || a.role == "student").map(a => {
    const session = grader.students.find(b => b._id == a._id)?.session;
    return {...a, session: session || a.session};
  });
  const sessions = useDatabaseSync({ query: {collection: "sessions", assessment_id: assessment._id}, original_value: [], enabled: true });

  const students = just_students.map(a => {
    const session = sessions.find(b => b.uid == a._id);
    return {...a, session: session || a.session};
  });

  const [sort, setSort] = useState(["name", -1]);

  const columns = [
    { name: "Name", id: "name", sortable: [
      { value: -1, name: "A to Z", arrow: "up", default: true },
      { value: 1, name: "Z to A", arrow: "down" }
    ]},
    { name: "Status", id: "status", width: 150, hide_order: 1, sortable: [
      { value: 1, name: "Not started first", arrow: "up", default: true },
      { value: -1, name: "Returned first", arrow: "down" }
    ]},
    { name: "Date submitted", id: "date", width: 200, hide_order: 0, sortable: [
      { value: -1, name: "Newest first", arrow: "down", default: true },
      { value: 1, name: "Oldest first", arrow: "up" }
    ]}
  ];

  const due = assessment.data.due_date;

  const sorted_students = useMemo(() => {
    return [...students].sort((a, b) => {
      const [columnId, sortValue] = sort;

      if (columnId === "name") {
        const cmp = (a.name || "").localeCompare(b.name || "");
        return sortValue === -1 ? cmp : -cmp;
      }

      if (columnId === "status") {
        const statusOrder = (s) => {
          if (!s.session) return 0;
          if (!s.session.submitted) return 1;
          if (!s.session.returned) return 2;
          return 3;
        };
        const cmp = statusOrder(a) - statusOrder(b);
        return sortValue === 1 ? cmp : -cmp;
      }

      if (columnId === "date") {
        const dateA = a.session?.submitted_date || a.session?.date || 0;
        const dateB = b.session?.submitted_date || b.session?.date || 0;
        return sortValue === -1 ? (dateB - dateA) : (dateA - dateB);
      }

      return 0;
    });
  }, [students, sort]);

  return (
    <div className="dashboard_content_inner nearest_scrollable_container">
      <List fullPage={true} columns={columns} sort={sort} setSort={setSort} menu={true}>
        {sorted_students.map(student => (
          <SubmissionItem key={student._id} student={student} due={due} course_id={course_id} assessment={assessment} privacyMode={privacyMode} />
        ))}
      </List>
      {sorted_students.length === 0 && <GenericEmptyListPlaceholder inline text="No submissions"/>}
    </div>
  )
}

function SubmissionItem({ student, due, course_id, assessment, privacyMode }) {
  const navigate = useNavigate();
  const { icon, iconColor } = getSubmissionIcon(student.session, due);

  const menu = useMenu({
    menuItems: [
      {
        icon: 'link',
        label: 'Copy link',
        onClick: () => {navigator.clipboard.writeText(`https://app.sapientclinician.com/courses/${course_id}/assessments/${assessment._id}/grader/${student._id}`)},
      },
    ]
  });

  return (
    <ListItem icon={icon} iconColor={iconColor} onClick={() => {navigate(`/courses/${course_id}/assessments/${assessment._id}/grader/${student._id}`)}} menu={menu}>
      <ItemColumn>{privacyMode ? "[Learner name]" : (student.name || "(No name)")}</ItemColumn>
      <ItemColumn>{getStatusText(student.session, due)}</ItemColumn>
      <ItemColumn>{getDateText(student.session)}</ItemColumn>
    </ListItem>
  )
}

function getSubmissionIcon(session, due) {
  if (!session) return { icon: "dotted-circle", iconColor: "var(--submission_not_started)" };
  if (!session.submitted) return { icon: "progress-clock", iconColor: "var(--submission_in_progress)" };
  if (session.returned) {
    if (due && due < session.submitted_date) return { icon: "check-clock", iconColor: "var(--submission_graded)" };
    return { icon: "check-circle", iconColor: "var(--submission_graded)" };
  }
  if (due && due < session.submitted_date) return { icon: "check-outline-clock", iconColor: "var(--submission_graded)" };
  return { icon: "check-circle-outline", iconColor: "var(--submission_graded)" };
}

function getStatusText(session, due) {
  if (!session) return "Not started";
  if (!session.submitted) return "In progress";
  if (session.returned) {
    if (due && due < session.submitted_date) return "Returned (late)";
    return "Returned";
  }
  if (due && due < session.submitted_date) return "Completed late";
  return "Completed";
}

function getDateText(session) {
  if (!session?.submitted) return "—";
  return formatDate(session.submitted_date);
}

export function AssessmentPreviews() {
  const { assessment_title, assessment_id, scenario_version_id, assessment_scenario_version_id, preview_sessions } = useLoaderData().data.value;
  const navigate = useNavigate();
  const { course_id } = useParams();

  return (
    <>
      <Header title={`Previews of "${assessment_title}"`} backButton={true}>
        <Button icon="play-outline" text="Preview" className="hollow" backgroundColor="var(--header)" onClick={() => {navigate(`/courses/${course_id}/assessments/${assessment_id}/preview`)}}/>
      </Header>
      <div className="dashboard_content_inner nearest_scrollable_container">
        <List fullPage={true} columns={[{ name: "Date", min_width: 200 }, { name: "Previewed by", width: 260, hide_order: 1 }]} menu={true}>
          {preview_sessions.map(session => <PreviewSessionItem key={session._id} session={session} scenario_version_id={scenario_version_id} assessment_scenario_version_id={assessment_scenario_version_id} full_size={true} is_editor={true} is_assessment={true} />)}
        </List>
        {preview_sessions.length === 0 && <GenericEmptyListPlaceholder inline text="No previews"/>}
      </div>
    </>
  )
}
